import React, { useMemo } from 'react';
import { UserAvatar } from '@t-one-internal/design-system';

interface LearningParticipant {
    employeeId: string;
    internalUserId: number;
    firstName: string;
    lastName: string;
    courseCount: number;
    completedCount: number;
    inProgressCount: number;
}

interface TeamLearningPathsListProps {
    data: Array<Record<string, unknown>>;
}

const toParticipant = (raw: Record<string, unknown>): LearningParticipant => ({
    employeeId: (raw.employee_id as string) ?? '',
    internalUserId: Number(raw.internal_user_id ?? 0),
    firstName: (raw.first_name as string) ?? '',
    lastName: (raw.last_name as string) ?? '',
    courseCount: Number(raw.course_count ?? 0),
    completedCount: Number(raw.completed_count ?? 0),
    inProgressCount: Number(raw.in_progress_count ?? 0),
});

const getProgressColor = (progress: number): string => {
    if (progress >= 70) return '#10B981'; // green
    if (progress >= 40) return '#F59E0B'; // amber
    return '#EF4444'; // red
};

const getStatus = (p: LearningParticipant): { label: string; color: string } => {
    if (p.courseCount === 0) return { label: 'No Courses', color: '#9CA3AF' };
    if (p.completedCount === p.courseCount) return { label: 'Completed', color: '#10B981' };
    if (p.inProgressCount > 0 || p.completedCount > 0) return { label: 'In Progress', color: '#F59E0B' };
    return { label: 'Not Started', color: '#EF4444' };
};

const getDisplayName = (p: LearningParticipant): string => {
    const first = p.firstName || 'Unknown';
    const lastInitial = p.lastName ? ` ${p.lastName.charAt(0)}.` : '';
    return `${first}${lastInitial}`;
};

export const TeamLearningPathsList: React.FC<TeamLearningPathsListProps> = ({ data }) => {
    const items = useMemo(() => {
        if (!data || !Array.isArray(data)) return [];
        return data.map(toParticipant);
    }, [data]);

    if (items.length === 0) {
        return (
            <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                No learning plans found
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full overflow-hidden">
            {/* Header row */}
            <div
                className="flex items-center gap-3 px-2 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider"
                style={{ flexShrink: 0 }}
            >
                <div style={{ width: '36px' }} className="shrink-0" />
                <div className="flex-1 min-w-0">PERSON</div>
                <div className="shrink-0 w-16 text-center">COURSES</div>
                <div className="shrink-0 w-24 text-center">PROGRESS</div>
                <div className="shrink-0 w-20 text-right">STATUS</div>
            </div>

            {/* Rows */}
            <div className="flex-1 overflow-auto min-h-0 space-y-0.5" style={{ scrollbarWidth: 'none' }}>
                {items.map((participant, index) => {
                    const progress = participant.courseCount > 0
                        ? Math.round((participant.completedCount / participant.courseCount) * 100)
                        : 0;
                    const status = getStatus(participant);

                    return (
                        <div
                            key={participant.employeeId + index}
                            className="flex items-center gap-3 px-2 py-2 hover:bg-gray-50/80 rounded-lg transition-colors"
                        >
                            <div className="shrink-0">
                                <UserAvatar
                                    name={`${participant.firstName} ${participant.lastName}`}
                                    userId={participant.internalUserId || participant.employeeId}
                                    size="lg"
                                />
                            </div>

                            <div className="flex-1 min-w-0">
                                <div className="text-[13px] font-semibold text-gray-900 truncate leading-tight">
                                    {getDisplayName(participant)}
                                </div>
                            </div>

                            <div className="shrink-0 w-16 text-center">
                                <span className="text-[12px] text-gray-600">
                                    {participant.courseCount}
                                </span>
                            </div>

                            <div className="shrink-0 w-24 flex items-center gap-1.5">
                                <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                    <div
                                        className="h-full rounded-full transition-all"
                                        style={{
                                            width: `${Math.min(progress, 100)}%`,
                                            backgroundColor: getProgressColor(progress),
                                        }}
                                    />
                                </div>
                                <span className="text-[11px] text-gray-500 shrink-0 w-7 text-right">
                                    {progress}%
                                </span>
                            </div>

                            <div className="shrink-0 w-20 text-right">
                                <span
                                    className="text-[11px] font-medium"
                                    style={{ color: status.color }}
                                >
                                    {status.label}
                                </span>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
